import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Mail, Send, CheckCircle2, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactFormModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      
      setSuccess(true);
      toast.success('Nachricht erfolgreich gesendet!');
      
      // Reset form after 2 seconds and close
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        setSuccess(false);
        onClose();
      }, 2000);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden der Nachricht');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToDashboard = () => {
    onClose();
    navigate('/dashboard');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
            <Mail className="w-6 h-6" style={{ color: '#1EA0E6' }} />
            Support Kontaktieren
          </DialogTitle>
        </DialogHeader>

        {success ? (
          <div className="text-center py-12">
            <CheckCircle2 className="w-16 h-16 mx-auto mb-4" style={{ color: '#22C55E' }} />
            <h3 className="text-xl font-semibold mb-2" style={{ color: '#E6EDF3' }}>
              Nachricht gesendet!
            </h3>
            <p style={{ color: '#94A3B8' }}>
              Wir werden uns so schnell wie möglich bei Ihnen melden.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" style={{ color: '#E6EDF3' }}>
                  Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Ihr Name"
                  required
                  style={{
                    backgroundColor: '#0B0F14',
                    borderColor: '#1F2937',
                    color: '#E6EDF3'
                  }}
                />
              </div>

              <div>
                <Label htmlFor="email" style={{ color: '#E6EDF3' }}>
                  E-Mail *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="ihre@email.de"
                  required
                  style={{
                    backgroundColor: '#0B0F14',
                    borderColor: '#1F2937',
                    color: '#E6EDF3'
                  }}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="subject" style={{ color: '#E6EDF3' }}>
                Betreff
              </Label>
              <Input
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Worum geht es?"
                style={{
                  backgroundColor: '#0B0F14',
                  borderColor: '#1F2937',
                  color: '#E6EDF3'
                }}
              />
            </div>

            <div>
              <Label htmlFor="message" style={{ color: '#E6EDF3' }}>
                Nachricht *
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Beschreiben Sie Ihr Anliegen..."
                rows={6}
                required
                style={{
                  backgroundColor: '#0B0F14',
                  borderColor: '#1F2937',
                  color: '#E6EDF3'
                }}
              />
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                onClick={handleBackToDashboard}
                variant="outline"
                className="flex-1"
                style={{ borderColor: '#1F2937', color: '#E6EDF3' }}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück zum Dashboard
              </Button>
              
              <Button
                type="submit"
                disabled={loading}
                className="flex-1"
                style={{ backgroundColor: '#1EA0E6', color: '#FFFFFF' }}
              >
                {loading ? (
                  <>Wird gesendet...</>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Nachricht senden
                  </>
                )}
              </Button>
            </div>

            <p className="text-xs text-center" style={{ color: '#94A3B8' }}>
              * Pflichtfelder
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
